import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ContractsListScreen.module.scss';
import { DefaultAssetImage, LOGGER_LOG_TYPE, MAX_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import { DataTableWithSidebar, DataTableColumn, OrderData } from 'common/components/dataTable';
import DateFormat from 'common/components/dateFormat/dateFormat';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import ReactTooltip from 'react-tooltip';
import InfoCard from 'common/components/infoCard/InfoCard';
import ContractsListingService from 'api/contracts/unifiedListing/ContractsListingService';
import { UnifiedContractListDto } from 'api/contracts/unifiedListing/models/UnifiedContractListDto';
import { UnifiedContractsSearchCriteria } from 'api/contracts/unifiedListing/models/UnifiedContractsSearchCriteria';
import { UnifiedContractsCountersDto } from 'api/contracts/unifiedListing/models/UnifiedContractsCountersDto';
import { ContractStatusColorEnum } from 'api/contracts/enums/ContractStatusColorEnum';
import ContractSearchIcon from 'assets/svg/desktop_contrat_search.svg';
import CircleCheckIcon from 'assets/svg/circle-check-regular.svg';
import AlertIcon from 'assets/svg/i_alertas_white.svg';
import CircleExclamationIcon from 'assets/svg/circle-exclamation-white.svg';
import DeletedVehicleCircle from 'common/components/deletedVehicleCircle/DeletedVehicleCircle';

// Extended type for table rows with all needed fields
type ContractTableRow = UnifiedContractListDto & {
    contractTypeDisplay: string;
    contractSubTypeDisplay: string;
    numberDisplay: string;
    stateDisplay: string;
    startDateDisplay: string;
    endDateDisplay: string;
    supplierDisplay: string;
    costDisplay: string;
    reminderDisplay: string;
    vehicleRegistration: string;
    vehicleBrand: string;
    vehicleModel: string;
    vehicleImage: string;
};

// Criteria fields that don't change the counters (they only affect ordering/pagination)
const COUNTERS_IGNORED_CRITERIA_KEYS = ['page', 'itemsPerPage', 'orderBy', 'orderColumn'];

// Fingerprint of the criteria filters only, so the counters are refreshed whenever any filter
// changes. Built from the actual keys (instead of a hardcoded list) so new filters are covered.
const getFiltersFingerprint = (criteria: UnifiedContractsSearchCriteria): string => {
    const keys = Object.keys(criteria).filter(k => !COUNTERS_IGNORED_CRITERIA_KEYS.includes(k)).sort();
    return JSON.stringify(criteria, keys);
};

export type Props = {
    criteria: UnifiedContractsSearchCriteria;
    onClickItem: (vehicleId: string, contractType: string, contractId: string) => void;
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
};

const ContractsListScreen: React.FC<Props> = ({ criteria: _criteria, onClickItem, exportRef }) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const [items, setItems] = useState<UnifiedContractListDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [counters, setCounters] = useState<UnifiedContractsCountersDto>();
    const [criteria, setCriteria] = useState<UnifiedContractsSearchCriteria>(_criteria);
    const countersLoaded = useRef<boolean>(false);
    const imageCacheKey = useRef(new Date().getTime());
    const currentPage = useRef(1);
    const previousCriteria = useRef<UnifiedContractsSearchCriteria>(_criteria);

    const translateContractTypeName = (typeName: string): string => {
        if (!typeName) return '-';
        const isSystemType = /^[A-Z_]+$/.test(typeName);
        if (isSystemType) {
            const insuranceTranslated = t(`common.insurance_contract_types.${typeName}` as any);
            if (insuranceTranslated !== `common.insurance_contract_types.${typeName}`) return insuranceTranslated;
            const maintenanceTranslated = t(`common.maintenance_contract_type.${typeName}` as any);
            if (maintenanceTranslated !== `common.maintenance_contract_type.${typeName}`) return maintenanceTranslated;
            const useTranslated = t(`common.use_contract_types.${typeName}` as any);
            if (useTranslated !== `common.use_contract_types.${typeName}`) return useTranslated;
        }
        return typeName;
    };

    const translateContractTypeNames = (names: string): string => {
        if (!names) return '-';
        return names.split(', ').map(n => translateContractTypeName(n.trim())).join(', ');
    };

    const getContractState = (row: UnifiedContractListDto): { label: string; color: string } => {
        const now = new Date();
        const start = new Date(row.startDate);
        const end = row.endDate ? new Date(row.endDate) : null;

        if (start > now) {
            return { label: t('contracts.states.planned') as string, color: ContractStatusColorEnum.PLANNED };
        }
        if (!end || (start <= now && end >= now)) {
            return { label: t('contracts.states.in_progress') as string, color: ContractStatusColorEnum.IN_PROGRESS };
        }
        return { label: t('contracts.states.expired') as string, color: ContractStatusColorEnum.EXPIRED };
    };

    const getContractTypeBadgeColor = (contractType: string): string => {
        switch (contractType) {
            case 'USE': return '#026a8f';
            case 'MAINTENANCE': return '#038cb6';
            case 'INSURANCE': return '#02c7c7';
            default: return '#999';
        }
    };

    const getContractTypeLabel = (contractType: string): string => {
        switch (contractType) {
            case 'USE': return t('contracts_listing.type_use') as string;
            case 'MAINTENANCE': return t('contracts_listing.type_maintenance') as string;
            case 'INSURANCE': return t('contracts_listing.type_insurance') as string;
            default: return contractType;
        }
    };

    const getPeriodicityLabel = (periodicity: string | undefined | null): string => {
        if (!periodicity) return '';
        const key = `common.periodicities.${periodicity}`;
        const translated = t(key as any) as string;
        return translated === key ? '' : translated;
    };

    const getData = async (isLoadMore = false) => {
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const res = await ContractsListingService.getList(criteria);
            setTotalItems(res.totalItems);

            if (isLoadMore) {
                setItems(prev => [...prev, ...res.items]);
            } else {
                setItems(res.items);
            }

            // Check if only order/page changed (not filters that affect counters)
            const onlyOrderOrPageChanged =
                getFiltersFingerprint(previousCriteria.current) === getFiltersFingerprint(criteria);

            const shouldLoadCounters = !countersLoaded.current || !onlyOrderOrPageChanged;
            if (shouldLoadCounters) {
                await getCounters();
                countersLoaded.current = true;
            }

            previousCriteria.current = criteria;
            ReactTooltip.rebuild();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CONTRACTS, `Couldn't get contracts list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            Loading.hide();
            setIsLoadingMore(false);
        }
    };

    const getCounters = async () => {
        try {
            const data = await ContractsListingService.getCounters(criteria);
            setCounters(data);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CONTRACTS, `Couldn't get contracts counters`, error);
        }
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setItems([]);
        setTotalItems(0);
        currentPage.current = 1;
        setCriteria(prev => ({ ...prev, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field }));
    };

    const loadMore = useCallback(() => {
        if (isLoadingMore || items.length >= totalItems) return;
        currentPage.current += 1;
        setCriteria(prev => ({ ...prev, page: currentPage.current }));
    }, [isLoadingMore, items.length, totalItems]);

    useEffect(() => {
        currentPage.current = 1;
        countersLoaded.current = false;
        setItems([]);
        setTotalItems(0);
        setCriteria({ ..._criteria, page: 1 });
    }, [_criteria]);

    useEffect(() => {
        const isLoadMore = criteria.page > 1 && items.length > 0;
        getData(isLoadMore);
    }, [criteria]);

    // Mapping function
    const toTableRow = (item: UnifiedContractListDto): ContractTableRow => ({
        ...item,
        contractTypeDisplay: getContractTypeLabel(item.contractType),
        contractSubTypeDisplay: translateContractTypeNames(item.contractTypeName),
        numberDisplay: item.number || '-',
        stateDisplay: getContractState(item).label,
        startDateDisplay: '',
        endDateDisplay: '',
        supplierDisplay: item.supplierName || '',
        costDisplay: '',
        reminderDisplay: item.reminderCount > 0 ? t('common.yes') as string : t('common.no') as string,
        vehicleRegistration: item.registrationNumber || '',
        vehicleBrand: item.vehicleBrandName || '',
        vehicleModel: item.vehicleModelName || '',
        vehicleImage: item.photoUrl || '',
    });

    const tableRows: ContractTableRow[] = useMemo(() => items.map(toTableRow), [items, t]);

    // Fetch all for export
    const fetchAllForExcelExport = useCallback(async (): Promise<ContractTableRow[]> => {
        const result = await ContractsListingService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items.map(toTableRow);
    }, [criteria]);

    const tableColumns: DataTableColumn<ContractTableRow>[] = useMemo(() => {
        const euroHeaderNote = `${t('common.unit')}: ${t('common.euro')}`;

        return [
            {
                field: 'vehicleImage',
                name: t('common.image'),
                hideOrderButton: true,
                width: 75,
                hideOn: ['sm', 'md'],
                excludeFromExport: true,
                renderCell: row =>
                    <div className={styles.imageDescCell}>
                        <div className={styles.contentImage}>
                            <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row.photoUrl ? 'url(' + row.photoUrl + '?_=' + imageCacheKey.current + ')' : row.vehicleSegmentDefaultPhotoUrl ? 'url(' + row.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                        </div>
                    </div>,
            },
            {
                field: 'vehicleRegistration',
                name: t('vehicle.registration') as string,
                orderColumn: 'v.registration_number',
                width: 130,
                renderCell: row =>
                    <div className={styles.descCell}>
                        <div className={styles.columnPlate}>
                            {row.registrationNumber || '-'}
                            {!row.vehicleActive && <DeletedVehicleCircle id={row.id ?? ''} />}
                        </div>
                    </div>,
            },
            {
                field: 'vehicleBrand',
                name: t('vehicle.brand') as string,
                orderColumn: 'vb.name',
                width: 100,
                hideOn: ['sm'],
                renderCell: row =>
                    <div className={styles.columnBrand}>
                        {row.vehicleBrandName || '-'}
                    </div>,
            },
            {
                field: 'vehicleModel',
                name: t('vehicle.model') as string,
                orderColumn: 'vm.name',
                width: 100,
                hideOn: ['sm'],
                renderCell: row =>
                    <div className={styles.columnBrand}>
                        {row.vehicleModelName || '-'}
                    </div>,
            },
            {
                field: 'vehicleSegmentName',
                name: t('vehicle.type') as string,
                orderColumn: 'vs.name',
                width: 130,
                renderCell: row => <>{row.vehicleSegmentName || '-'}</>,
            },
            {
                field: 'vehicleLocalNames',
                name: t('vehicle.local') as string,
                hideOrderButton: true,
                width: 130,
                renderCell: row => <>{row.vehicleLocalNames || '-'}</>,
            },
            {
                field: 'contractTypeDisplay',
                name: t('contracts_listing.column_contract_type') as string,
                orderColumn: 'c.contract_type',
                width: 120,
                exportValue: row => getContractTypeLabel(row.contractType),
                renderCell: row =>
                    <span
                        className={styles.contractTypeBadge}
                        style={{ backgroundColor: getContractTypeBadgeColor(row.contractType) }}
                    >
                        {getContractTypeLabel(row.contractType)}
                    </span>,
            },
            {
                field: 'contractSubTypeDisplay',
                name: t('contracts_listing.column_sub_type') as string,
                orderColumn: 'c.contract_type_name',
                width: 130,
                hideOn: ['sm', 'md'],
                exportValue: row => translateContractTypeNames(row.contractTypeName),
                renderCell: row => <>{translateContractTypeNames(row.contractTypeName)}</>,
            },
            {
                field: 'numberDisplay',
                name: t('common.number') as string,
                orderColumn: 'c.number',
                width: 100,
                renderCell: row => <>{row.number || '-'}</>,
            },
            {
                field: 'stateDisplay',
                name: t('common.state') as string,
                orderColumn: 'c.end_date',
                width: 100,
                exportValue: row => getContractState(row).label,
                renderCell: row => {
                    const state = getContractState(row);
                    return (
                        <div className={styles.status} style={{ color: state.color }}>
                            {state.label}
                        </div>
                    );
                },
            },
            {
                field: 'startDateDisplay',
                name: t('common.start') as string,
                orderColumn: 'c.start_date',
                width: 110,
                hideOn: ['sm', 'md'],
                exportValue: row => row.startDate ? new Date(row.startDate).toLocaleDateString() : '-',
                renderCell: row =>
                    <>{row.startDate ? <DateFormat format="DD/MM/yyyy" value={row.startDate} /> : <>-</>}</>,
            },
            {
                field: 'endDateDisplay',
                name: t('common.end') as string,
                orderColumn: 'c.end_date',
                width: 110,
                hideOn: ['sm', 'md'],
                exportValue: row => row.endDate ? new Date(row.endDate).toLocaleDateString() : '-',
                renderCell: row =>
                    <>{row.endDate ? <DateFormat format="DD/MM/yyyy" value={row.endDate} /> : <>-</>}</>,
            },
            {
                field: 'supplierDisplay',
                name: t('contracts_listing.column_supplier') as string,
                orderColumn: 'supplier.name',
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.supplierName || '-',
                renderCell: row => <>
                    <div data-for="supplierTooltip" data-tip={row.supplierUrl ? (row.supplierName || '-') : undefined}>
                        {row.supplierUrl && <img src={row.supplierUrl} style={{ height: '2rem' }} alt="" />}
                        {!row.supplierUrl && (row.supplierName || '-')}
                    </div>
                </>,
            },
            {
                field: 'costDisplay',
                name: t('contracts_listing.column_cost') as string,
                orderColumn: 'c.cost',
                cellAlignment: 'right',
                columnCellAlignment: 'right',
                width: 120,
                hideOn: ['sm', 'md'],
                headerNote: euroHeaderNote,
                exportValue: row => row.cost ?? 0,
                renderCell: row => {
                    if (!row.cost) return <>-</>;
                    const periodicityLabel = getPeriodicityLabel(row.periodicity);
                    const suffix = periodicityLabel ? `${t('common.euro')}/${periodicityLabel}` : (t('common.euro') as string);
                    return <MoneyFormat fixedDecimalScale={true} value={row.cost} suffix={suffix} />;
                },
            },
            {
                field: 'costCumule',
                name: t('contracts_listing.column_accumulated_cost') as string,
                orderColumn: 'cost_cumule',
                cellAlignment: 'right',
                columnCellAlignment: 'right',
                width: 120,
                hideOn: ['sm', 'md'],
                headerNote: euroHeaderNote,
                exportValue: row => row.costCumule ?? 0,
                renderCell: row => row.costCumule
                    ? <MoneyFormat fixedDecimalScale={true} value={row.costCumule} suffix={t('common.euro')} />
                    : <>-</>,
            },
            {
                field: 'reminderDisplay',
                name: t('contracts_listing.column_reminder') as string,
                orderColumn: 'c.reminder_count',
                width: 100,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.reminderCount > 0 ? t('common.yes') as string : t('common.no') as string,
                renderCell: row => {
                    const tooltipId = `reminder-${row.id}`;
                    const hasReminder = row.reminderCount > 0;
                    return (
                        <>
                            <span data-tip data-for={tooltipId}>
                                {hasReminder ? t('common.yes') : t('common.no')}
                            </span>
                            {hasReminder && (
                                <ReactTooltip id={tooltipId} effect="solid" place="top">
                                    {t('contracts_listing.reminder_count', { count: row.reminderCount })}
                                </ReactTooltip>
                            )}
                        </>
                    );
                },
            },
        ];
    }, [t]);

    // Handle order change from DataTable
    const handleOrderChange = (orderData: OrderData<ContractTableRow>) => {
        const orderColumn = orderData.orderColumn || (orderData.field as string);
        onTableFilter(orderColumn, orderData.isOrderAsc);
    };

    // Map orderColumn to field for proper sorting indicator
    const getFieldFromOrderColumn = (orderColumn: string | undefined): keyof ContractTableRow => {
        const mapping: Record<string, keyof ContractTableRow> = {
            'v.registration_number': 'vehicleRegistration',
            'vb.name': 'vehicleBrand',
            'vm.name': 'vehicleModel',
            'vs.name': 'vehicleSegmentName',
            'c.contract_type': 'contractTypeDisplay',
            'c.contract_type_name': 'contractSubTypeDisplay',
            'c.number': 'numberDisplay',
            'c.end_date': 'endDateDisplay',
            'c.start_date': 'startDateDisplay',
            'supplier.name': 'supplierDisplay',
            'c.cost': 'costDisplay',
            'cost_cumule': 'costCumule',
            'c.reminder_count': 'reminderDisplay',
        };
        return mapping[orderColumn ?? ''] ?? 'startDateDisplay';
    };

    // Get default order from criteria
    const defaultOrder: OrderData<ContractTableRow> = useMemo(() => ({
        field: getFieldFromOrderColumn(criteria.orderColumn),
        orderColumn: criteria.orderColumn ?? 'c.start_date',
        isOrderAsc: criteria.orderBy === 'asc',
    }), [criteria.orderColumn, criteria.orderBy]);

    return (
        <div className={styles.listWrapper}>
            <div className={styles.countersSection}>
                <div className={styles.rowCounters}>
                    {counters && (
                        <InfoCard
                            label={t('contracts_listing.counter_total') as string}
                            value={counters.totalContracts ?? 0}
                            icon={ContractSearchIcon}
                            backgroundColor="#02c7c7"
                        />
                    )}
                    {counters && (
                        <InfoCard
                            label={t('contracts_listing.counter_in_progress') as string}
                            value={counters.inProgressContracts ?? 0}
                            icon={CircleCheckIcon}
                            backgroundColor="#0cb6d1"
                        />
                    )}
                    {counters && (
                        <InfoCard
                            label={t('contracts_listing.counter_expired') as string}
                            value={counters.expiredContracts ?? 0}
                            icon={AlertIcon}
                            backgroundColor="#e04f5f"
                        />
                    )}
                    {counters && (
                        <InfoCard
                            label={t('contracts_listing.counter_without_reminder') as string}
                            value={counters.withoutReminderContracts ?? 0}
                            icon={CircleExclamationIcon}
                            backgroundColor="#e8a824"
                        />
                    )}
                </div>
            </div>
            <div className={styles.listTblWrapper}>
                <DataTableWithSidebar
                    cacheKey="contracts-listing-v2"
                    columns={tableColumns}
                    rows={tableRows}
                    defaultHiddenColumns={['vehicleSegmentName', 'vehicleLocalNames']}
                    defaultOrder={defaultOrder}
                    onRowClick={row => onClickItem(row.vehicleId, row.contractType, row.id)}
                    onOrderChange={handleOrderChange}
                    onHref={row => `/vehicles/details/${row.vehicleId}/contract/details/${row.contractType.toLowerCase()}/details/${row.id}`}
                    onRowId={row => row.id ?? ''}
                    infiniteScroll={{
                        isLoading: isLoadingMore,
                        hasMore: items.length < totalItems,
                        onLoadMore: loadMore,
                    }}
                    exportExcel={{
                        fetchAll: fetchAllForExcelExport,
                        fileName: t('contracts_listing.title') as string,
                    }}
                    exportRef={exportRef}
                    hideExportInSidebar={!!exportRef}
                />
            </div>
            <ReactTooltip id="supplierTooltip" effect="solid" />
        </div>
    );
};

export default ContractsListScreen;
